/**
 * Pure LCS (Longest Common Subsequence) Line-by-Line Diff Algorithm for FWB EDI messages.
 */

function normalizeLine(line = '') {
  return typeof line === 'string'
    ? line
      .replace(/\r/g, '')
      .replace(/[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ')
      .trimEnd()
    : '';
}

export function computeLineDiff(oldText = '', newText = '') {
  const oldLines = typeof oldText === 'string' ? oldText.split(/\r?\n/) : [];
  const newLines = typeof newText === 'string' ? newText.split(/\r?\n/) : [];

  const m = oldLines.length;
  const n = newLines.length;

  // DP table for LCS
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i < m; i++) {
    for (let j = 0; j < n; j++) {
      if (normalizeLine(oldLines[i]) === normalizeLine(newLines[j])) {
        dp[i + 1][j + 1] = dp[i][j] + 1;
      } else {
        dp[i + 1][j + 1] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  // Backtrack to assemble the diff
  let i = m;
  let j = n;
  const result = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && normalizeLine(oldLines[i - 1]) === normalizeLine(newLines[j - 1])) {
      result.unshift({
        type: 'unchanged',
        line: newLines[j - 1] || oldLines[i - 1],
        oldLineNumber: i,
        newLineNumber: j,
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({
        type: 'added',
        line: newLines[j - 1],
        oldLineNumber: null,
        newLineNumber: j,
      });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      result.unshift({
        type: 'removed',
        line: oldLines[i - 1],
        oldLineNumber: i,
        newLineNumber: null,
      });
      i--;
    }
  }

  const addedCount = result.filter((r) => r.type === 'added').length;
  const removedCount = result.filter((r) => r.type === 'removed').length;
  const hasChanges = addedCount > 0 || removedCount > 0;

  return {
    diff: result,
    addedCount,
    removedCount,
    hasChanges,
  };
}

export function computeSideBySideDiff(oldText = '', newText = '') {
  const { diff, addedCount, removedCount, hasChanges } = computeLineDiff(oldText, newText);

  const rows = [];
  let i = 0;

  while (i < diff.length) {
    const item = diff[i];

    if (item.type === 'unchanged') {
      rows.push({
        left: {
          type: 'unchanged',
          line: item.line,
          lineNumber: item.oldLineNumber,
        },
        right: {
          type: 'unchanged',
          line: item.line,
          lineNumber: item.newLineNumber,
        },
      });
      i++;
    } else {
      const removedBlock = [];
      const addedBlock = [];

      while (i < diff.length && diff[i].type !== 'unchanged') {
        if (diff[i].type === 'removed') {
          removedBlock.push(diff[i]);
        } else if (diff[i].type === 'added') {
          addedBlock.push(diff[i]);
        }
        i++;
      }

      const maxLen = Math.max(removedBlock.length, addedBlock.length);
      for (let k = 0; k < maxLen; k++) {
        const rem = removedBlock[k] || null;
        const add = addedBlock[k] || null;

        rows.push({
          left: rem
            ? {
              type: 'removed',
              line: rem.line,
              lineNumber: rem.oldLineNumber,
            }
            : {
              type: 'empty',
              line: '',
              lineNumber: null,
            },
          right: add
            ? {
              type: 'added',
              line: add.line,
              lineNumber: add.newLineNumber,
            }
            : {
              type: 'empty',
              line: '',
              lineNumber: null,
            },
        });
      }
    }
  }

  return {
    rows,
    diff,
    addedCount,
    removedCount,
    hasChanges,
  };
}

export function groupDiffHunks(oldText = '', newText = '') {
  const { diff, addedCount, removedCount, hasChanges } = computeLineDiff(oldText, newText);

  const elements = [];
  const hunks = [];
  let i = 0;
  let hunkIndex = 0;

  while (i < diff.length) {
    const item = diff[i];

    if (item.type === 'unchanged') {
      elements.push({
        type: 'unchanged',
        line: item.line,
        oldLineNumber: item.oldLineNumber,
        newLineNumber: item.newLineNumber,
      });
      i++;
    } else {
      // Group contiguous removed and added lines into a single hunk
      const oldLines = [];
      const newLines = [];
      let oldStartLine = null;
      let newStartLine = null;

      while (i < diff.length && diff[i].type !== 'unchanged') {
        const cur = diff[i];
        if (cur.type === 'removed') {
          if (oldStartLine === null) oldStartLine = cur.oldLineNumber;
          oldLines.push({
            line: cur.line,
            lineNumber: cur.oldLineNumber,
          });
        } else if (cur.type === 'added') {
          if (newStartLine === null) newStartLine = cur.newLineNumber;
          newLines.push({
            line: cur.line,
            lineNumber: cur.newLineNumber,
          });
        }
        i++;
      }

      let hunkType = 'replace';
      if (oldLines.length === 0 && newLines.length > 0) hunkType = 'insert';
      if (oldLines.length > 0 && newLines.length === 0) hunkType = 'delete';

      const hunk = {
        id: `hunk-${hunkIndex++}`,
        type: hunkType,
        oldLines,
        newLines,
        oldStartLine: oldStartLine !== null ? oldStartLine : (newStartLine || 1),
        newStartLine: newStartLine !== null ? newStartLine : (oldStartLine || 1),
        decision: 'pending', // 'pending' | 'accepted' | 'rejected'
      };

      hunks.push(hunk);
      elements.push({
        type: 'hunk',
        hunk,
      });
    }
  }

  return {
    diff,
    elements,
    hunks,
    hasChanges,
    addedCount,
    removedCount,
  };
}

export function applyHunkDecisions(elements = [], hunkDecisions = {}) {
  const resultLines = [];

  for (const el of elements) {
    if (el.type === 'unchanged') {
      resultLines.push(el.line);
    } else if (el.type === 'hunk') {
      const hunk = el.hunk;
      const decision = hunkDecisions[hunk.id] !== undefined ? hunkDecisions[hunk.id] : (hunk.decision || 'pending');

      if (decision === 'accepted') {
        for (const item of hunk.newLines) {
          resultLines.push(typeof item === 'string' ? item : item.line);
        }
      } else {
        // rejected or pending: preserve original oldLines
        for (const item of hunk.oldLines) {
          resultLines.push(typeof item === 'string' ? item : item.line);
        }
      }
    }
  }

  return resultLines.join('\n');
}

export function extractChangedLines(oldText = '', newText = '') {
  if (!oldText && !newText) return [];
  const { diff } = computeLineDiff(oldText, newText);
  return diff
    .filter((d) => d.type === 'removed' || d.type === 'added')
    .map((d) => ({
      type: d.type,
      line: d.line,
      lineNumber: d.type === 'added' ? d.newLineNumber : d.oldLineNumber,
    }));
}

